import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../utils/axiosConfig';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

type DaysRange = 7 | 30 | 90;

interface EngagementByTypePoint {
  date: string;
  mainQuizzes: number;
  subQuizQuizzes: number;
  leagueQuizzes: number;
  otherQuizzes: number;
}

type QuizTypeSeriesKey = keyof Omit<EngagementByTypePoint, 'date'>;

interface QuizTypeEngagementLineChartProps {
  className?: string;
  days?: DaysRange;
  includeBots?: boolean;
  seriesKey: QuizTypeSeriesKey;
  label: string;
  stroke?: string;
}

const formatDateLabel = (raw: string): string => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const QuizTypeEngagementLineChart: React.FC<QuizTypeEngagementLineChartProps> = ({
  className,
  days = 30,
  includeBots = false,
  seriesKey,
  label,
  stroke = '#6366f1'
}) => {
  const [data, setData] = useState<EngagementByTypePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get<{ success: boolean; data: EngagementByTypePoint[] }>(
          '/api/admin/metrics/engagement/trends-by-type',
          { params: { days, includeBots } }
        );
        if (!res.data.success) {
          throw new Error('Failed to load engagement by type');
        }
        if (!cancelled) {
          setData(res.data.data || []);
        }
      } catch (err: any) {
        console.error('[QuizTypeEngagementLineChart] Failed to load trends', err);
        if (!cancelled) setError('Failed to load engagement trends.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [days, includeBots]);

  const trimmed = useMemo(() => data.map((d) => ({ date: d.date, value: Number(d[seriesKey]) || 0 })), [data, seriesKey]);

  if (loading && trimmed.length === 0) {
    return (
      <div className={className}>
        <div className="admin-dashboard-card__meta">Loading engagement…</div>
      </div>
    );
  }

  if (error && trimmed.length === 0) {
    return (
      <div className={className}>
        <div className="admin-dashboard-card__meta">{error}</div>
      </div>
    );
  }

  if (trimmed.length === 0) {
    return (
      <div className={className}>
        <div className="admin-dashboard-card__meta">No engagement yet.</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trimmed} margin={{ top: 10, right: 20, left: 0, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-subtle, #e5e7eb)" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateLabel}
              stroke="var(--text-muted, #6b7280)"
              tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 11 }}
              height={35}
            />
            <YAxis
              stroke="var(--text-muted, #6b7280)"
              tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 11 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--surface-elevated, #ffffff)',
                border: '1px solid var(--surface-subtle, #e5e7eb)',
                borderRadius: 4,
                fontSize: 12
              }}
              labelFormatter={(l: string) => formatDateLabel(l)}
              formatter={(v: any) => [v, label]}
            />
            <Line type="monotone" dataKey="value" stroke={stroke} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default QuizTypeEngagementLineChart;






























